/**
 * ComponentRegistry — maps string names to React component constructors.
 *
 * Used by the TemplateEngine to detect "React island" nodes inside a template
 * tree and accumulate mount metadata for later hydration.
 *
 * Each TemplateEngine instance owns one ComponentRegistry so registrations
 * are always scoped to a single engine and never bleed across instances.
 */
export class ComponentRegistry {
    /**
     * Creates an empty registry.
     */
    constructor() {
        /** @type {Map<string, React.ComponentType>} */
        this._map = new Map()
    }

    /**
     * Registers a React component under the given name.
     * Calling register() twice with the same name overwrites the previous entry.
     *
     * @param {string} name - Identifier used as a node name in the template tree.
     * @param {React.ComponentType} component - React component constructor or function.
     */
    register(name, component) {
        if (typeof name !== "string" || !name.trim()) {
            throw new Error("ComponentRegistry.register: name must be a non-empty string")
        }
        if (typeof component !== "function") {
            throw new Error(`ComponentRegistry.register: component for "${name}" must be a function`)
        }
        this._map.set(name, component)
    }

    /**
     * Returns the registered React component for the given name.
     *
     * @param {string} name - Component identifier.
     * @returns {React.ComponentType|undefined} The component, or undefined if not registered.
     */
    get(name) {
        return this._map.get(name)
    }

    /**
     * Returns true if a component is registered under the given name.
     *
     * @param {string} name - Component identifier.
     * @returns {boolean}
     */
    has(name) {
        return this._map.has(name)
    }

    /**
     * Removes all registered components.
     * Useful for cleanup in tests or when re-initialising an engine instance.
     */
    clear() {
        this._map.clear()
    }
}
